import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plane, Search, Plus, ChevronLeft, ChevronRight, Eye, Trash2, Download, List, CalendarDays, Filter, UserCheck, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRIP_STATUS_LABELS, TRIP_STATUS_VARIANT, SOURCES, PARTNER_LABELS, fmtDate } from "@/pages/trips/shared";
import TripDetail from "@/pages/trips/TripDetail";
import TripsCalendar from "@/pages/trips/TripsCalendar";
import { TripKimKimImport } from "@/pages/trips/TripKimKimImport";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { DestinationMultiSelect, displayDestination } from "@/components/DestinationMultiSelect";

const LIMIT = 20;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { main_traveler: "", agent: "", destination: "", source: "", partner: "other", status: "abierto", number_of_travelers: 1, fx_rate: "", phone: "" };

// Roles que ven TODOS los trips por defecto (directores, admin general, socios).
// El resto (travel_specialist, agente_reservas, traveler_support, ...) arranca
// filtrado a "mis viajes" para cargar rápido lo relevante a su día a día.
const OVERSIGHT_ROLES = new Set(["dir_admin_general", "director_sales", "dir_operaciones", "socio_director"]);
const TRIPS_FILTERS_STORAGE_KEY = "sofi.trips.filters.v1";

function readStoredFilters() {
  try {
    const raw = localStorage.getItem(TRIPS_FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function computeDefaults(user) {
  // Prioridad 1: preferencia del usuario guardada (persiste entre sesiones).
  const stored = readStoredFilters();
  if (stored && typeof stored === "object") {
    return {
      status: typeof stored.status === "string" ? stored.status : "abierto",
      mine: !!stored.mine,
      source: typeof stored.source === "string" ? stored.source : "all",
    };
  }
  // Prioridad 2: defaults por rol para el primer acceso.
  const isOversight = OVERSIGHT_ROLES.has(user?.role);
  return {
    status: "abierto",
    mine: !isOversight,   // agentes operativos → solo los suyos por defecto
    source: "all",
  };
}

export default function TripsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const defaults = React.useMemo(() => computeDefaults(user), [user]);
  // URL como fuente de verdad para saber qué trip está abierto. Permite:
  //   · Compartir un enlace directo a un trip / reserva / tab con el equipo
  //   · Ctrl-click sobre una fila → abre en pestaña nueva
  //   · Refresh de la página → mantiene el estado
  // Al mismo tiempo `setSelected(null)` (volver) borra `trip/tab/reserva`
  // del querystring para dejar la lista limpia. (2026-08 · multitab)
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get("trip") || null;
  const setSelected = (id) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (id) {
        p.set("trip", id);
      } else {
        p.delete("trip");
        p.delete("tab");
        p.delete("reserva");
      }
      return p;
    });
  };
  const [view, setView] = useState("list");
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(defaults.status);
  const [source, setSource] = useState(defaults.source);
  const [mine, setMine] = useState(defaults.mine);
  const [agentF, setAgentF] = useState("all");
  const [dateField, setDateField] = useState("start_date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [kimkimImportOpen, setKimkimImportOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  // Persistencia de filtros clave (status / mine / source) en localStorage:
  // así, si el usuario cambia una vez a "todos", la próxima visita respeta
  // esa preferencia y no vuelve a filtrar por defecto.
  useEffect(() => {
    try {
      localStorage.setItem(TRIPS_FILTERS_STORAGE_KEY, JSON.stringify({ status, mine, source }));
    } catch { /* localStorage puede estar deshabilitado — fallback silencioso */ }
  }, [status, mine, source]);

  const resetToShowAll = () => {
    setStatus("all"); setSource("all"); setMine(false);
    setAgentF("all"); setDateField("start_date"); setDateFrom(""); setDateTo("");
    setPage(0);
    try {
      localStorage.setItem(TRIPS_FILTERS_STORAGE_KEY, JSON.stringify({ status: "all", mine: false, source: "all" }));
    } catch { /* no-op */ }
  };

  useEffect(() => { const t = setTimeout(() => { setQ(term); setPage(0); }, 350); return () => clearTimeout(t); }, [term]);

  const metaQ = useQuery({ queryKey: ["trips-meta"], queryFn: async () => (await api.get("/trips/meta")).data });
  const partnersQ = useQuery({ queryKey: ["formula-partners"], queryFn: async () => (await api.get("/formulas/partners")).data });
  const listQ = useQuery({
    queryKey: ["trips", q, status, source, mine, agentF, dateField, dateFrom, dateTo, page],
    queryFn: async () => (await api.get("/trips", { params: {
      q, status: status === "all" ? "" : status, source: source === "all" ? "" : source,
      mine: mine || undefined, agent: agentF === "all" ? "" : agentF,
      date_field: dateField, date_from: dateFrom || "", date_to: dateTo || "",
      skip: page * LIMIT, limit: LIMIT,
    } })).data,
    keepPreviousData: true,
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const partners = partnersQ.data?.partners || [];
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const advCount = (mine ? 1 : 0) + (agentF !== "all" ? 1 : 0) + (dateFrom || dateTo ? 1 : 0);
  const clearAdv = () => { setMine(false); setAgentF("all"); setDateField("start_date"); setDateFrom(""); setDateTo(""); setPage(0); };

  const saveMut = useMutation({
    mutationFn: () => api.post("/trips", { ...form, number_of_travelers: parseInt(form.number_of_travelers) || 1, fx_rate: form.fx_rate ? parseFloat(form.fx_rate) : null }),
    onSuccess: (res) => {
      toast.success("Viaje creado");
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["trips-meta"] });
      setOpen(false);
      setSelected(res.data.id);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/trips/${id}`),
    onSuccess: () => { toast.success("Viaje eliminado"); qc.invalidateQueries({ queryKey: ["trips"] }); qc.invalidateQueries({ queryKey: ["trips-meta"] }); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (selected) {
    return <TripDetail tripId={selected} onBack={() => { setSelected(null); qc.invalidateQueries({ queryKey: ["trips"] }); }} />;
  }

  const statuses = metaQ.data?.statuses || [];

  return (
    <div className="space-y-5" data-testid="trips-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <Plane className="h-6 w-6 text-primary" /> Trips
          </h1>
          <p className="text-sm text-muted-foreground">Viajes, viajeros y reservas con contabilidad en vivo · Iteración 4</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border p-0.5">
            <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} className="h-7 px-2" onClick={() => setView("list")} data-testid="view-list"><List className="h-4 w-4" /></Button>
            <Button size="sm" variant={view === "calendar" ? "secondary" : "ghost"} className="h-7 px-2" onClick={() => setView("calendar")} data-testid="view-calendar"><CalendarDays className="h-4 w-4" /></Button>
          </div>
          <a href={`${BACKEND}/api/trips/export.csv`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="trips-export-csv"><Download className="mr-1.5 h-4 w-4" />CSV</Button>
          </a>
        </div>
      </div>

      {view === "calendar" ? (
        <TripsCalendar onSelectTrip={setSelected} />
      ) : (
      <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <div className="relative sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar por cliente, destino, agente…" className="pl-8" data-testid="trip-search" />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
            <SelectTrigger className="sm:w-44" data-testid="trip-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s}>{TRIP_STATUS_LABELS[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={(v) => { setSource(v); setPage(0); }}>
            <SelectTrigger className="sm:w-40" data-testid="trip-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las fuentes</SelectItem>
              {(metaQ.data?.sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant={mine ? "secondary" : "outline"}
            onClick={() => { setMine((m) => !m); setAgentF("all"); setPage(0); }}
            data-testid="trips-mine-toggle"
          >
            <UserCheck className="mr-1.5 h-4 w-4" />Mis viajes
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" data-testid="trips-filters-button">
                <Filter className="mr-1.5 h-4 w-4" />Más filtros
                {advCount > 0 && <Badge className="ml-1.5 h-5 px-1.5 text-[10px]" data-testid="trips-filters-count">{advCount}</Badge>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start" data-testid="trips-filters-panel">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Filtros avanzados</p>
                  {advCount > 0 && <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={clearAdv} data-testid="trips-filters-clear"><X className="mr-1 h-3.5 w-3.5" />Limpiar</Button>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Agente asignado</Label>
                  <Select value={agentF} onValueChange={(v) => { setAgentF(v); setMine(false); setPage(0); }}>
                    <SelectTrigger data-testid="trips-agent-filter"><SelectValue placeholder="Agente" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">Todos los agentes</SelectItem>
                      {(metaQ.data?.agents || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Separator />
                <div className="space-y-1.5">
                  <Label className="text-xs">Rango de fechas</Label>
                  <Select value={dateField} onValueChange={(v) => { setDateField(v); setPage(0); }}>
                    <SelectTrigger data-testid="trips-datefield-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="start_date">Inicio del viaje</SelectItem>
                      <SelectItem value="booking_date">Fecha de reserva</SelectItem>
                      <SelectItem value="created_at">Fecha de creación</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Desde</Label><Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} data-testid="trips-date-from" /></div>
                    <div className="space-y-1"><Label className="text-[11px] text-muted-foreground">Hasta</Label><Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} data-testid="trips-date-to" /></div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setKimkimImportOpen(true)} data-testid="import-kimkim-button" className="border-primary/40 text-primary hover:bg-primary/5">
            <Wand2 className="mr-1.5 h-4 w-4" />Import from KimKim
          </Button>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-trip-button"><Plus className="mr-1.5 h-4 w-4" />Nuevo viaje</Button>
        </div>
      </div>

      {/* Aviso de filtros por defecto activos · botón para ver TODOS. Persiste
          la elección del usuario en localStorage. */}
      {(status !== "all" || mine || source !== "all") && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs"
          data-testid="trips-default-filter-banner"
        >
          <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
            <span>Mostrando</span>
            {status !== "all" && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                {TRIP_STATUS_LABELS[status] || status}
              </Badge>
            )}
            {mine && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                mis viajes
              </Badge>
            )}
            {source !== "all" && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                {source}
              </Badge>
            )}
            <span className="ml-1">· {total} resultado{total === 1 ? "" : "s"}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={resetToShowAll}
            data-testid="trips-show-all-btn"
          >
            Ver todos los viajes
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="trips-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Plane className="h-6 w-6" /></div>
              <p className="font-semibold">Sin viajes</p>
              <p className="text-sm text-muted-foreground">Ajusta los filtros o crea un viaje nuevo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="trips-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Agente</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Reservas</TableHead>
                    <TableHead>Fechas</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((t) => {
                    // URL absoluta al módulo trips para que el `<a href>` siga
                    // funcionando aunque estemos en otra sub-ruta de `/app/*`.
                    const tripUrl = `/app/trips?trip=${t.id}`;
                    const isPlainLeftClick = (e) =>
                      e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
                    const handleRowClick = (e) => {
                      if (!isPlainLeftClick(e)) return;         // Cmd/Ctrl/Shift → dejar al navegador
                      if (e.target.closest("[data-row-actions]")) return;
                      if (e.target.closest("a")) return;        // el <a> se gestiona a sí mismo
                      setSelected(t.id);
                    };
                    return (
                    <TableRow key={t.id} className="cursor-pointer" onClick={handleRowClick} data-testid={`trip-row-${t.id}`}>
                      <TableCell className="font-medium">
                        <a
                          href={tripUrl}
                          onClick={(e) => {
                            // Solo interceptamos el click-primario simple.
                            // Cmd/Ctrl/Shift/Middle-click → el navegador abre en pestaña nueva.
                            // Botón derecho → contextmenu, ni siquiera pasa por onClick.
                            if (!isPlainLeftClick(e)) return;
                            e.preventDefault();
                            setSelected(t.id);
                          }}
                          className="text-inherit hover:text-primary"
                          data-testid={`trip-link-${t.id}`}
                        >
                          {t.main_traveler}
                        </a>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.agent || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{displayDestination(t.destination) || "—"}</TableCell>
                      <TableCell className="text-sm">{t.source || "—"}</TableCell>
                      <TableCell><Badge variant={TRIP_STATUS_VARIANT[t.status] || "secondary"} className="text-[10px]">{TRIP_STATUS_LABELS[t.status] || t.status}</Badge></TableCell>
                      <TableCell className="font-mono text-[11px] text-muted-foreground">{t.status_summary || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(t.start_date)}</TableCell>
                      <TableCell className="text-right" data-row-actions onClick={(e) => e.stopPropagation()}>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSelected(t.id)} data-testid={`view-trip-${t.id}`} aria-label="Ver"><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(t)} data-testid={`delete-trip-${t.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} data-testid="trips-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Pág. {page + 1} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="trips-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <span data-testid="trips-count">{total} viajes</span>
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="trip-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo viaje</DialogTitle>
            <DialogDescription>Datos generales del viaje. Luego añade viajeros y reservas.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Cliente (Lead) *</Label><Input value={form.main_traveler} onChange={(e) => set("main_traveler", e.target.value)} data-testid="trip-name-input" /></div>
            <div className="space-y-1.5"><Label>Agente de ventas</Label><Input value={form.agent} onChange={(e) => set("agent", e.target.value)} data-testid="trip-agent-input" /></div>
            <div className="space-y-1.5"><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Destino</Label><DestinationMultiSelect value={form.destination} onChange={(v) => set("destination", v)} testid="trip-destination-input" /></div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={form.source || "none"} onValueChange={(v) => set("source", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="trip-source-input"><SelectValue placeholder="Fuente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Partner (markup)</Label>
              <Select value={form.partner} onValueChange={(v) => set("partner", v)}>
                <SelectTrigger data-testid="trip-partner-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="trip-status-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TRIP_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Nº viajeros</Label><Input type="number" min="1" value={form.number_of_travelers} onChange={(e) => set("number_of_travelers", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cambio €/$ (opcional)</Label><Input type="number" step="0.0001" value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} placeholder="1.08" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.main_traveler || saveMut.isPending} data-testid="save-trip-button">{saveMut.isPending ? "Creando…" : "Crear viaje"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-trip-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar viaje?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{delTarget?.main_traveler}” junto con sus viajeros y reservas. No se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-trip">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TripKimKimImport
        open={kimkimImportOpen}
        onClose={() => setKimkimImportOpen(false)}
        onCreated={(tripId) => {
          setKimkimImportOpen(false);
          qc.invalidateQueries({ queryKey: ["trips"] });
          setSelected(tripId);
        }}
      />
    </div>
  );
}
